import { ApiError } from '../../lib/apiClient';
import { DosingConflictError } from '../../services/dosingScheduleService';

/** Form fields a backend validation error can point at. */
export type DosingFormField =
  | 'productName'
  | 'doseAmount'
  | 'doseUnit'
  | 'weekdays'
  | 'intervalDays'
  | 'timesPerDay'
  | 'timeOfDay'
  | 'doseTimes'
  | 'startDate'
  | 'endDate'
  | 'notes';

/** `error.field` of a 400 VALIDATION_ERROR (API body names) → form field. */
const API_FIELD_TO_FORM_FIELD: ReadonlyMap<string, DosingFormField> = new Map([
  ['product_name', 'productName'],
  ['dose_amount', 'doseAmount'],
  ['dose_unit', 'doseUnit'],
  ['weekdays', 'weekdays'],
  ['interval_days', 'intervalDays'],
  ['times_per_day', 'timesPerDay'],
  ['time_of_day', 'timeOfDay'],
  ['dose_times', 'doseTimes'],
  ['start_date', 'startDate'],
  ['end_date', 'endDate'],
  ['notes', 'notes'],
]);

export interface DosingSaveError {
  /** i18n key of the reason shown under the "could not be saved" title. */
  readonly messageKey: string;
  readonly params?: Readonly<Record<string, string>>;
  /** Set when the backend rejected one specific field. */
  readonly field?: DosingFormField;
}

interface ApiErrorBody {
  readonly error?: { readonly field?: unknown };
}

function apiField(data: unknown): string | null {
  const field = (data as ApiErrorBody | undefined)?.error?.field;
  return typeof field === 'string' ? field : null;
}

/**
 * Maps a failed create/update to a translated reason. Never surfaces backend
 * text: every outcome is an i18n key (+ the conflicting product's name, which is
 * the user's own data).
 */
export function toDosingSaveError(err: unknown): DosingSaveError {
  if (err instanceof DosingConflictError) {
    return err.existingProductName
      ? { messageKey: 'dosing.form.errors.conflict', params: { product: err.existingProductName } }
      : { messageKey: 'dosing.form.errors.conflictGeneric' };
  }
  if (err instanceof ApiError) {
    if (err.status === 400) {
      const name = apiField(err.data);
      const field = name !== null ? API_FIELD_TO_FORM_FIELD.get(name) : undefined;
      return field ? { messageKey: 'dosing.form.errors.validation', field } : { messageKey: 'dosing.form.errors.validation' };
    }
    if (err.status === 401) return { messageKey: 'dosing.form.errors.sessionExpired' };
    if (err.status === 403) return { messageKey: 'dosing.form.errors.forbidden' };
    if (err.status === 404) return { messageKey: 'dosing.form.errors.notFound' };
    if (err.status === 0) return { messageKey: 'dosing.form.errors.network' };
    if (err.status >= 500) return { messageKey: 'dosing.form.errors.server' };
  }
  if (err instanceof TypeError) return { messageKey: 'dosing.form.errors.network' };
  return { messageKey: 'dosing.form.genericError' };
}
